/**
 * header.js
 * Two header-shell concerns that other parts of the theme depend on.
 *
 * 1. Cart count badge — the badge is always in the DOM (initial value from
 *    cart.item_count) and hidden at zero via the --zero modifier. TWO external
 *    writers set its text without touching that class: the Rebuy Smart Cart
 *    drawer, and assets/cart.js `updateHeaderCount()`. Without this observer
 *    the badge stays hidden until a full page reload. Only childList and
 *    characterData are observed, never attributes, so toggling the class here
 *    cannot re-trigger the observer.
 *
 * 2. Sticky header + directional announcement reveal — stickiness itself is
 *    CSS on the section wrapper (see the comment on
 *    `.section-header:has(.header--sticky)` in sections/header.liquid); this
 *    only toggles the state classes that CSS reacts to, and publishes the
 *    announcement height the condense transform reads.
 *
 * DOM contract consumed by five other files — do not rename without updating
 * them: `.header--sticky` (page-nav.js, hero-navigation.js,
 * hero-anchor-scroll.js), `[data-header]` (sticky-add-to-cart.js),
 * `.header__cart-count` + `--zero` (cart.js, Rebuy), and `.section-header`
 * (product.js).
 *
 * Re-inits on `shopify:section:load`. Every listener is bound through a
 * per-element AbortController so an editor save replaces its listeners rather
 * than stacking a new set on window each time.
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 990; // matches the nav↔drawer switch in the section CSS

  function initCartCount(scope) {
    (scope || document).querySelectorAll('.header__cart-count').forEach(function (el) {
      if (el.__cartCountObserver) el.__cartCountObserver.disconnect();

      var sync = function () {
        var count = parseInt(el.textContent.trim(), 10);
        el.classList.toggle('header__cart-count--zero', !count || Number.isNaN(count));
      };

      sync();
      var observer = new MutationObserver(sync);
      observer.observe(el, { childList: true, characterData: true, subtree: true });
      el.__cartCountObserver = observer;
    });
  }

  function initSticky(header) {
    if (header.__headerAbort) header.__headerAbort.abort();

    var section = header.closest('.shopify-section');
    if (!section || !header.classList.contains('header--sticky')) return;

    var ac = (header.__headerAbort = new AbortController());
    var signal = ac.signal;

    var announcementHeight = 0;
    var lastY = window.pageYOffset;
    var scrollTicking = false;

    var measure = function () {
      // The bar's offset within the header equals the height of the
      // announcement region stacked above it. Measured as a rect delta rather
      // than offsetTop: offsetTop is relative to the nearest *positioned*
      // ancestor, so wrapping the bar in anything position:relative would
      // silently make this 0 and kill the condense transform with no error.
      var bar = header.querySelector('[data-header-bar], .header__nav-bar');
      if (!bar) {
        console.warn('[header] no [data-header-bar] found; condense disabled');
        announcementHeight = 0;
      } else {
        announcementHeight = Math.round(
          bar.getBoundingClientRect().top - header.getBoundingClientRect().top
        );
      }
      section.style.setProperty('--header-announcement-height', announcementHeight + 'px');
    };

    var update = function () {
      scrollTicking = false;
      var y = window.pageYOffset;

      // Condensing is mobile-only. Near the top, or with no announcement
      // region, the header stays fully revealed.
      if (window.innerWidth >= DESKTOP_MIN || announcementHeight <= 0 || y <= announcementHeight) {
        header.classList.remove('header--condensed');
      } else if (y > lastY + 4) {
        header.classList.add('header--condensed'); // scrolling down
      } else if (y < lastY - 4) {
        header.classList.remove('header--condensed'); // scrolling up
      }

      lastY = y;
    };

    var onScroll = function () {
      if (!scrollTicking) {
        window.requestAnimationFrame(update);
        scrollTicking = true;
      }
    };

    measure();
    update();
    window.addEventListener('scroll', onScroll, { passive: true, signal: signal });
    window.addEventListener(
      'resize',
      function () {
        measure();
        update();
      },
      { signal: signal }
    );
  }

  function initAll(scope) {
    initCartCount(scope);
    var header = (scope || document).querySelector('[data-header]');
    if (header) initSticky(header);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
